import { Box, Chip, Grid2 as Grid, Tab, Tabs, Typography, Checkbox, Stack, Button, Tooltip } from "@mui/material";
import React from "react";
import { useAppDispatch, useAppSelector } from "../../redux";
import { updateSelectedChip, clearSelectedChip, selectAllChip, SelectedDependency, DepKeys } from "../../redux/slices/utils";
import { PublishedIcon } from "../../images/svg/publishedIcon";
import { WIPIcon } from "../../images/svg/WIPIcon";

interface AssociationArray {
    object_type: DepKeys,
    object_details: any
}

const versionSupportedEntity = ["material", "bom", "quote", "document"];

export const DeletePanel: React.FC<{
    children?: JSX.Element,
    message?: string,
    warningMessage?: string,
    errorMessage?: string,
    errorMessageStacks?: AssociationArray[],
    chipSelectable?: boolean
    isMaterial?: boolean
    errorLinkingMessage?: string,
    parentTabs?: {
        value: string,
        label: string,
        options: AssociationArray[]
    }[],
    entity?: "bom" | "material";
}> = ({
    message, errorMessage, parentTabs,
    warningMessage, chipSelectable = false, isMaterial = false, errorLinkingMessage = "", entity,
    ...props
}) => {

        const dispatch = useAppDispatch();
        const { selectedChip } = useAppSelector(store => store.utils);

        const [tab, updateTab] = React.useState(0);
        const [parentTab, updateParentTab] = React.useState(0);
        const [errorMessageStacks, setErrorMessageStacks] = React.useState<AssociationArray[]>([]);

        React.useEffect(() => {
            updateTab(0);

            if (props.errorMessageStacks && !parentTabs) {
                setErrorMessageStacks(props.errorMessageStacks)
            } else if (parentTabs) {
                updateParentTab(0);
                setErrorMessageStacks(parentTabs?.[0]?.options ?? []);
            }
            // eslint-disable-next-line
        }, [props.errorMessageStacks, parentTabs])

        React.useEffect(() => {
            if (entity === "bom" || isMaterial) {
                const mapped: Partial<SelectedDependency> = {};
                parentTabs?.forEach(p => {
                    p?.options?.forEach(e => {
                        mapped[e.object_type as DepKeys] = e.object_details.filter((_: any) => {
                            return true;
                        }).map((_: any) => _._id)
                    });
                })
                dispatch(selectAllChip(mapped as Partial<SelectedDependency>));
            }
            // eslint-disable-next-line
        }, [entity])

        return (
            (<Box>
                {message && <Typography gutterBottom >{message}</Typography>}
                <Box sx={{
                    display: "flex",
                    flexDirection: parentTabs ? "row" : "column"
                }}>
                    {parentTabs && <Box>
                        <Tabs
                            orientation="vertical"
                            value={parentTab}
                            onChange={(e, tab) => {
                                updateParentTab(tab);
                                setErrorMessageStacks(parentTabs?.[tab]?.options ?? []);
                                updateTab(0);
                            }}
                            indicatorColor="primary"
                            textColor="primary"
                            sx={{
                                background: (theme) => theme.palette.background.default,
                                border: (theme) => `1px solid ${theme.palette.divider}`,
                                borderTopLeftRadius: 8,
                                borderBottomLeftRadius: 8
                            }}
                        >
                            {parentTabs?.map((_parentTab, index) => <Tab id={_parentTab.label} sx={{ background: parentTab === index ? "white" : "" }} key={index} value={index} label={_parentTab.label} />)}
                        </Tabs>
                    </Box>}

                    <Box
                        sx={{
                            p: parentTabs ? 2 : 0,
                            border: (theme) => parentTabs ? `1px solid ${theme.palette.divider}` : ``,
                            minWidth: '300px'
                        }}>
                        {errorMessageStacks.length > 0 && <>
                            <Tabs
                                value={tab}
                                onChange={(e, tab) => updateTab(tab)}
                                indicatorColor="primary"
                                textColor="primary"
                                sx={{
                                    background: (theme) => theme.palette.background.default,
                                    border: (theme) => `1px solid ${theme.palette.divider}`,
                                    borderTopLeftRadius: 8, borderTopRightRadius: 8
                                }}
                            >
                                {errorMessageStacks?.map((stack, index) => <Tab sx={{ background: tab === index ? "white" : "" }} key={index} value={index} label={stack.object_type} />)}
                            </Tabs>

                            <Box
                                sx={{
                                    p: 2,
                                    border: (theme) => `1px solid ${theme.palette.divider}`
                                }}>
                                <Grid container spacing={1}>
                                    {isMaterial && errorMessageStacks[tab].object_type === "bom" && <Typography variant="caption" color="error">{errorLinkingMessage}</Typography>}
                                    {errorMessageStacks[tab].object_details.map((det: any, index: any) => {
                                        if (typeof det === "string") {
                                            return (<Grid key={index}>
                                                <Chip
                                                    variant="outlined"
                                                    label={det}
                                                />
                                            </Grid>)
                                       
                                        }
                                        let objectType = errorMessageStacks[tab].object_type;
                                        const disableCheckBox = parentTabs?.[parentTab]?.value === 'parents' && det.released === true;
                                        return (
                                            (<Grid key={index}>
                                                <Chip
                                                    icon={chipSelectable ?

                                                        <Tooltip arrow placement="top" title={disableCheckBox ? `Published parent ${objectType} is not allowed to be linked to new version` : `Check to link`}>
                                                            <Box>
                                                                <Checkbox
                                                                    key={`${(selectedChip?.[objectType] || [])?.includes(det._id)}`}
                                                                    checked={selectedChip?.[objectType]?.includes(det._id)}
                                                                    disabled={disableCheckBox}
                                                                    onChange={(e) => dispatch(updateSelectedChip({ _id: det._id, key: objectType, index: selectedChip[objectType]?.indexOf(det._id) as number, checked: e.target.checked }))}
                                                                />
                                                            </Box>
                                                        </Tooltip> : <></>}
                                                    variant="outlined"
                                                    label={<Stack direction={"row"} sx={{
                                                        alignItems: "center"
                                                    }}>
                                                        {`${det.sequence_id}${typeof det?.version === "number" && versionSupportedEntity.includes(objectType) ? `-r${det.version}` : ""}`}
                                                        {det.released ? <PublishedIcon /> : <WIPIcon />}
                                                    </Stack>}
                                                    disabled={isMaterial || entity === "bom"}
                                                />
                                            </Grid>)
                                        );
                                    })}
                                </Grid>
                            </Box>
                            {chipSelectable && entity !== "bom" && !isMaterial && <Stack direction={"row"} spacing={1} sx={{
                                mt: 1
                            }}>
                                <Button
                                    id="select-all"
                                    size="small"
                                    variant="contained"
                                    onClick={(e) => {
                                        if (isMaterial && errorMessageStacks[tab]?.object_type === "bom") {
                                            return false;
                                        }

                                        const mapped: Partial<SelectedDependency> = {};
                                        errorMessageStacks.forEach(e => {
                                            mapped[e.object_type as DepKeys] = (e.object_type === "bom" && isMaterial) ? [] : e.object_details.filter((_: any) => {
                                                if (parentTabs && parentTabs[parentTab].value === 'parents' && _.released) {
                                                    return false;
                                                } else {
                                                    return true;
                                                }
                                            }).map((_: any) => _._id)
                                        });

                                        dispatch(selectAllChip(mapped as Partial<SelectedDependency>));
                                    }}
                                >
                                    Select All
                                </Button>
                                <Button size="small" variant="outlined" onClick={(e) => dispatch(clearSelectedChip())} >Unselect All</Button>
                            </Stack>}
                        </>}

                        {props.errorMessageStacks && errorMessageStacks.length === 0 && <Typography variant="caption">Nothing to show</Typography>}
                    </Box>
                </Box>
                {errorMessage && <Typography sx={{
                    mt: 1
                }}>{errorMessage}</Typography>}
                {warningMessage && <Typography variant="caption" color="orange">{warningMessage}</Typography>}
            </Box>)
        );
    }